import type { ConversationDto } from '@dijitalapp/shared';

/*
 * Where chats and channels live, as in WhatsApp: the chat list holds people and groups, ordered by their last
 * message (or, with none yet, by when I joined); channels I follow live in Keşfet (WhatsApp's Updates) unless I
 * chose to see them among my chats too (Settings › Sohbetler, Telegram style). Their unread posts light up
 * Keşfet, not Mesajlar. Pure functions, shared by the store, the lists and the badges.
 */

type Ordered = Pick<ConversationDto, 'lastMessage' | 'activityAt'> & { group?: { createdAt?: string } | null };

/** A chat's place in the list: its last visible message, else when I joined (older servers: when it was made). */
export function activityKey(c: Ordered): string {
  return c.lastMessage?.createdAt ?? c.activityAt ?? c.group?.createdAt ?? '';
}

/** Newest activity first (a stable order for equal times). */
export function byActivity(a: Ordered, b: Ordered): number {
  return activityKey(b).localeCompare(activityKey(a));
}

/** Whether a conversation belongs in the chat list: everything but channels, unless I asked for them there. */
export function inChatList(c: Pick<ConversationDto, 'type'>, channelsInChats: boolean): boolean {
  return channelsInChats || c.type !== 'channel';
}

type Counted = Pick<ConversationDto, 'type' | 'unreadCount' | 'markedUnread' | 'archived' | 'mutedUntil' | 'request'>;

/** Mesajlar's count: chats with something unread (muted, archived and waiting requests not counted). */
export function unreadChats(list: readonly Counted[], channelsInChats: boolean): number {
  return list.filter((c) => inChatList(c, channelsInChats) && (c.unreadCount > 0 || c.markedUnread) && !c.archived && !c.mutedUntil && !c.request?.incoming).length;
}

/**
 * New requests for Mesajlar's count and the chat list's "İstekler" entry (as Instagram): message requests with
 * something I have not opened yet, plus people who added me since I last opened İstekler.
 */
export function newRequests(list: readonly Counted[], addedMeNew: number): number {
  return list.filter((c) => c.request?.incoming && (c.unreadCount > 0 || c.markedUnread)).length + addedMeNew;
}

/** The tab title's and app icon's number: unread messages in the chats Mesajlar counts. */
export function unreadMessages(list: readonly Counted[], channelsInChats: boolean): number {
  return list.filter((c) => inChatList(c, channelsInChats) && !c.mutedUntil && !c.archived && !c.request?.incoming).reduce((n, c) => n + c.unreadCount, 0);
}

/** Keşfet's count: followed channels with new posts (muted ones not counted). */
export function unreadChannels(list: readonly Counted[]): number {
  return list.filter((c) => c.type === 'channel' && (c.unreadCount > 0 || c.markedUnread) && !c.mutedUntil).length;
}

/** The channels I follow, latest update first (Keşfet's lists). */
export function followedChannels<T extends Ordered & Pick<ConversationDto, 'type'>>(list: readonly T[]): T[] {
  return list.filter((c) => c.type === 'channel').sort(byActivity);
}

/**
 * How a followed channel's row looks: `new` (posts I have not seen: accent ring, bold, accent count), `muted` (new
 * posts in a channel I muted: the same in quiet grey) or `read`.
 */
export type ChannelState = 'new' | 'muted' | 'read';

export function channelState(c: Pick<ConversationDto, 'unreadCount' | 'markedUnread' | 'mutedUntil'>): ChannelState {
  if (c.unreadCount <= 0 && !c.markedUnread) return 'read';
  return c.mutedUntil ? 'muted' : 'new';
}

/** Keşfet's "channels I follow": those with new posts first, then muted ones with new posts, then the rest; each by latest update. */
export function channelsNewFirst<T extends Ordered & Pick<ConversationDto, 'type' | 'unreadCount' | 'markedUnread' | 'mutedUntil'>>(list: readonly T[]): T[] {
  const rank = { new: 0, muted: 1, read: 2 } as const;
  return followedChannels(list).sort((a, b) => rank[channelState(a)] - rank[channelState(b)] || byActivity(a, b));
}

/**
 * Where I may send or forward something: chats I can write in, and of channels only those I run (only their
 * admins post), as in WhatsApp; never a request waiting for my answer.
 */
export function canPostTo(c: Pick<ConversationDto, 'type' | 'canSend' | 'request'> & { group?: { myRole?: string } | null }): boolean {
  if (!c.canSend || c.request?.incoming) return false;
  return c.type !== 'channel' || c.group?.myRole === 'owner' || c.group?.myRole === 'admin';
}

/** The panel's default for people who never chose: remote config `channelsInChatList` (1: shown, else hidden). */
export function channelsInChatsDefault(remote: Record<string, unknown> | undefined): boolean {
  return remote?.channelsInChatList === 1;
}
